import Analytics from '@/utils/analytics';
import CreditRequestIcon from '@assets/icons/credit-request.svg';
import i18n from '@/utils/i18n';
import InvoiceIcon from '@assets/icons/invoice.svg';
import MenuIcon from '@assets/icons/menu.svg';
import PaymentsIcon from '@assets/icons/payments.svg';
import React, { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import {
  APP_CONTAINER_ROUTES,
  APP_ROUTES,
  CREDIT_REQUEST_INVOICE_ROUTES,
  PAYMENT_ROUTES,
} from '@/navigators/routes';
import { Button } from '@/components/button';
import { CheckInButton } from '@/components/check-in-button';
import { createStyles } from '@/contexts/theme-context';
import {
  CreditRequestByInvoice_SearchVariantPacksQuery,
  CreditRequestByInvoice_SearchVariantPacksQueryVariables,
  FulfillmentName,
  Invoice_GetInvoiceByIdQuery,
  Invoice_GetInvoiceByIdQueryVariables,
} from '@/types';
import {
  derivePartialPaymentAmount,
  displayInvoiceTotal,
  isPepperInvoicePDFHidden,
} from '@/utils/invoice';
import {
  FONTS,
  SCREEN_PADDING,
  SCREEN_PADDING_SMALL,
  TYPOGRAPHY,
} from '@/styles';
import { formatCurrency, formatUnitCount } from '@/utils/money';
import { formatDate } from '@/utils/date';
import { GET_INVOICE_DETAIL_QUERY } from './invoice-detail-query';
import { getOrderDate, isPastDateOrToday } from '@/utils/order-helpers';
import { InvoiceDetailList } from '@/components/payments/invoice-detail-list';
import { Props as NavigationHeaderContentProps } from '@/components/navigation-header-content';
import { OfflineComponent } from '@/components/offline-component';
import { OrderDetailPrices } from '@/screens/order-detail/order-detail-prices';
import { OrderFulfillmentDate } from '@/components/orders/order-fulfillment-date';
import { OrderInfo } from '@/components/orders/order-info';
import {
  OrderInvoiceAction,
  OrderInvoiceActionListOption,
  OrderInvoiceActions,
} from '@/components/orders/order-invoice-actions';
import { OrderInvoiceActionsSheet } from '@/components/orders/order-invoice-actions-sheet';
import { OrderInvoicePDFButton } from '@/components/orders/order-invoice-pdf-button';
import { PageContainer } from '@/components/page-container';
import { PaymentsStackParams } from '@/navigators/types';
import { Platform, StyleProp, Text, View, ViewStyle } from 'react-native';
import { SEARCH_VP_FOR_CREDIT_REQUEST_BY_INVOICE_QUERY } from '@/hooks/credit-requests/credit-request-queries';
import { StackNavigationProp } from '@react-navigation/stack';
import { translateFulfillmentType } from '@domain/fulfillment-settings/utils/translate-fulfillment-type';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import {
  useGetDynamicConfigValueForChat,
  useSupplierDynamicConfigValue,
} from '@/hooks/use-dynamic-config-value';
import { useGetOpenInvoicePDF } from '@/hooks/use-get-open-invoice-pdf';
import { useInitiateFTNI } from '@/hooks/use-initiate-ftni';
import { useItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNavigation } from '@react-navigation/native';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { usePaymentsContext } from '@/contexts/payments-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

interface Props {
  invoiceUUID: string;
  navigationHeaderContainerStyle?: StyleProp<ViewStyle>;
  navigationHeaderOverrides?: NavigationHeaderContentProps | null;
  renderPayable?: boolean;
}

export const InvoiceDetailComponent: React.FC<Props> = ({
  invoiceUUID,
  navigationHeaderContainerStyle,
  navigationHeaderOverrides,
  renderPayable = true,
}) => {
  const [showMoreActionsSheet, setShowMoreActionsSheet] = useState(false);
  const { isSmallLayout, isLargeLayout } = useLayoutContext();
  const { getDynamicConfigValueForChat } = useGetDynamicConfigValueForChat();
  const { navigate } =
    useNavigation<StackNavigationProp<PaymentsStackParams>>();
  const { currencyCode, setPaymentContextState } = usePaymentsContext();
  const itemQueryVariables = useItemQueryVariables();
  const { bottom } = useSafeAreaInsets();
  const {
    selectedRestaurantRelationship: {
      appPaymentsEnabled,
      employeeRole,
      showCreditRequest,
      supplierUUID,
    },
    selectedRestaurantUUID,
  } = useAuthenticatedUserContext();
  const isOfflineModeActive = useOfflineModeActive();
  const [styles, theme] = useStyles();

  const { data, loading, hasResponse } = useWrappedQuery<
    Invoice_GetInvoiceByIdQuery,
    Invoice_GetInvoiceByIdQueryVariables
  >(GET_INVOICE_DETAIL_QUERY, {
    variables: {
      invoiceUUID,
    },
  });

  const invoice = data?.getOrderInvoiceById.order_invoice;
  const disablePepperInvoicePdf = useSupplierDynamicConfigValue(
    'disable_pepper_invoice_pdf',
    false,
  );
  const showInvoicePDF = !isPepperInvoicePDFHidden({
    disablePepperInvoicePdf: Boolean(disablePepperInvoicePdf),
    hasCustomPdf: Boolean(invoice?.document_url),
  });
  const chatUUID = invoice?.chat_uuid;
  const existingCheckIn = invoice?.order_invoice_check_in;
  const order = data?.getOrderInvoiceById.order;
  const orderFulfillmentDisplayName =
    data?.getOrderInvoiceById.translated_order_fulfillment_display_name;

  const invoiceOrOrderItems = invoice?.order_invoice_line_items;

  const variantPackUUIDs = (invoiceOrOrderItems ?? []).reduce<string[]>(
    (acc, item) => {
      if (item.variant_pack_uuid) {
        acc.push(item.variant_pack_uuid);
      }
      return acc;
    },
    [],
  );

  const { data: creditRequestInvoiceItemsData } = useWrappedQuery<
    CreditRequestByInvoice_SearchVariantPacksQuery,
    CreditRequestByInvoice_SearchVariantPacksQueryVariables
  >(SEARCH_VP_FOR_CREDIT_REQUEST_BY_INVOICE_QUERY, {
    skip:
      !showCreditRequest ||
      !selectedRestaurantUUID ||
      !supplierUUID ||
      variantPackUUIDs.length === 0,
    variables: {
      ...itemQueryVariables,
      code: '', // because we are using orderInvoiceUUID
      orderInvoiceUUID: invoiceUUID,
      restaurantUUID: selectedRestaurantUUID,
      supplierUUID,
    },
  });

  const someItemsAreEligibleForCredit =
    creditRequestInvoiceItemsData?.searchVariantPacksForCreditRequest.some(
      (vp) => vp.eligible_for_credit,
    );

  const checkInConfig = chatUUID
    ? getDynamicConfigValueForChat(chatUUID, 'check_in_config')
    : null;
  const consumptionTax = chatUUID
    ? getDynamicConfigValueForChat(chatUUID, 'consumption_tax')
    : null;
  const ftniEnabled = chatUUID
    ? getDynamicConfigValueForChat(chatUUID, 'payments_ftni')
    : false;

  const isTodayOrPastInvoice = useMemo(() => {
    if (invoice) {
      const invoiceDate = getOrderDate(invoice);
      return isPastDateOrToday(invoiceDate);
    }
    return false;
  }, [invoice]);

  const showCheckInEntry =
    isTodayOrPastInvoice &&
    Platform.OS !== 'web' &&
    checkInConfig?.check_in_enabled;

  const currentBalance = invoice?.order_invoice_status?.use_pepper_balance
    ? invoice.order_invoice_status.balance_in_micros!
    : invoice?.erp_balance_in_micros!;
  const partialPaymentAmountInMicros = derivePartialPaymentAmount(
    invoice,
    currentBalance,
  );
  const partialPaymentDisplay =
    partialPaymentAmountInMicros !== null
      ? formatCurrency(partialPaymentAmountInMicros, currencyCode)
      : null;
  const totalBalance = displayInvoiceTotal(
    invoice?.order_invoice_line_items || [],
    currencyCode,
    consumptionTax,
  );

  const canPayInvoice = renderPayable && appPaymentsEnabled && employeeRole;

  const openInvoicePDF = useGetOpenInvoicePDF(
    data?.getOrderInvoiceById.public_url,
    invoice?.uuid,
    invoice?.seen,
  );

  // Setup FTNI payments for distributors using it
  const initiateFTNI = useInitiateFTNI();
  const onHandleInitiateFTNI = useCallback(() => {
    (async () => {
      if (invoice) {
        try {
          await initiateFTNI([invoice!.invoice_number!], [invoice!.uuid]);
        } catch (e) {
          console.error(e);
        }
      }
    })();
    navigate(APP_ROUTES.Payments, { screen: PAYMENT_ROUTES.Base });
  }, [initiateFTNI, invoice, navigate]);

  const onPayInvoice = useCallback(() => {
    setPaymentContextState({
      autoShowModal: true,
      selectedInvoiceUUIDs: new Set([invoice!.uuid]),
    });
    navigate(APP_ROUTES.Payments, { screen: PAYMENT_ROUTES.Base });
  }, [invoice, navigate, setPaymentContextState]);

  const payButton = useMemo(
    () => (
      <Button
        onPress={ftniEnabled ? onHandleInitiateFTNI : onPayInvoice}
        trackingData={{
          name: 'invoice_detail.pay_invoice_button',
          properties: {
            invoiceUUID: invoice?.uuid,
          },
        }}
      >
        {i18n.t('pay_invoice')}
      </Button>
    ),
    [ftniEnabled, invoice, onHandleInitiateFTNI, onPayInvoice],
  );

  const isCreditable =
    Platform.OS !== 'web' &&
    showCreditRequest &&
    someItemsAreEligibleForCredit &&
    isTodayOrPastInvoice;

  const moreActions: OrderInvoiceActionListOption[] = useMemo(() => {
    const actions: OrderInvoiceActionListOption[] = [];

    if (showInvoicePDF) {
      actions.push({
        metadata: {
          Icon: InvoiceIcon,
          iconProps: {
            stroke: theme.sk.color.icon.primary,
          },
          onPress: async () => {
            Analytics.track('invoice_detail.view_pdf_button', {
              invoiceUUID,
            });
            await openInvoicePDF();
          },
          stackedSheet: false,
        },
        title: i18n.t('view_invoice_pdf'),
        value: OrderInvoiceAction.VIEW_INVOICE_PDF,
      });
    }

    if (canPayInvoice) {
      actions.push({
        metadata: {
          Icon: PaymentsIcon,
          iconProps: {
            stroke: theme.sk.color.icon.primary,
          },
          onPress: () => {
            Analytics.track('invoice_detail.pay_invoice_button', {
              invoiceUUID,
            });
            if (ftniEnabled) {
              onHandleInitiateFTNI();
            } else {
              onPayInvoice();
            }
          },
          stackedSheet: true,
        },
        title: i18n.t('pay_invoice'),
        value: OrderInvoiceAction.PAY_INVOICE,
      });
    }

    if (isCreditable) {
      actions.push({
        metadata: {
          Icon: CreditRequestIcon,
          iconProps: {
            stroke: theme.sk.color.icon.primary,
          },
          onPress: () => {
            navigate(APP_CONTAINER_ROUTES.CreditRequestInvoiceItems, {
              params: {
                invoiceUUID,
                orderInvoiceUUID: invoice?.uuid,
                title: i18n.t('invoice_number', {
                  number: invoice?.invoice_number,
                }),
              },
              screen: CREDIT_REQUEST_INVOICE_ROUTES.CreditRequestInvoiceItems,
            });
          },
          stackedSheet: true,
        },
        title: i18n.t('credit_request'),
        value: OrderInvoiceAction.CREDIT_REQUEST,
      });
    }

    return actions;
  }, [
    canPayInvoice,
    ftniEnabled,
    invoice,
    invoiceUUID,
    isCreditable,
    navigate,
    onHandleInitiateFTNI,
    onPayInvoice,
    openInvoicePDF,
    showInvoicePDF,
    theme.sk.color.icon.primary,
  ]);

  const navigationHeaderProps = useMemo<NavigationHeaderContentProps>(() => {
    if (loading || !invoice) {
      return {
        showBackButton: isSmallLayout,
        showShadow: false,
      };
    }

    const renderInvoiceTitle = () => (
      <Text style={[TYPOGRAPHY.h2, styles.issuedOnDate]}>
        {renderPayable
          ? i18n.t('payment_pay_by_date', {
              date: formatDate(invoice.due_at),
            })
          : formatDate(invoice.invoice_date)}
      </Text>
    );

    const renderOrderSummaryComponent = () =>
      order ? (
        <>
          <Text style={[TYPOGRAPHY.medium, styles.navigationHeaderSubtitle]}>
            {i18n.t('order_title_next', {
              customerFacingId: order.customer_facing_id,
            })}
            {' • '}
            <OrderFulfillmentDate
              dateTranslator={(orderDate) =>
                translateFulfillmentType(
                  order?.fulfillment_type as FulfillmentName,
                  'styled_fulfillment_on',
                  orderFulfillmentDisplayName,
                  orderDate,
                )
              }
              order={order}
              textStyle={styles.navigationHeaderSubtitle}
            />
          </Text>
          <OrderInfo
            placedAt={order.placed_at}
            placedBy={
              order.placed_by
                ? {
                    name:
                      order.placed_by.supplier_employee_overrides[0]?.name ||
                      order.placed_by.name,
                    uuid: order.placed_by.uuid,
                  }
                : null
            }
          />
        </>
      ) : null;

    const invoiceUnitCount = invoice.order_invoice_line_items.reduce(
      (result, oili) => result + oili.ship_quantity,
      0,
    );

    if (isSmallLayout) {
      return {
        actions: [
          {
            Icon: MenuIcon,
            iconProps: {
              testID: 'invoice-detail-menu-button',
            },
            onPress: () => {
              Analytics.track('invoice_detail.menu_button.press');
              setShowMoreActionsSheet(true);
            },
          },
        ],
        centerTitle: true,
        contentFooter: (
          <>
            <View style={styles.invoiceInfoContainer}>
              {renderInvoiceTitle()}
              {renderOrderSummaryComponent()}
            </View>
            <View style={styles.totalRow}>
              <Text style={styles.totalStandard}>{i18n.t('total')}</Text>
              <Text style={styles.totalEmphasis}>{totalBalance}</Text>
            </View>
          </>
        ),
        showBackButton: true,
        title: (
          <Text style={[TYPOGRAPHY.navigationCenterTitle, styles.titleLeft]}>
            {i18n.t('invoice_number', {
              number: invoice.invoice_number,
            })}
          </Text>
        ),
        ...navigationHeaderOverrides,
      };
    }

    return {
      actions: !isLargeLayout
        ? [
            {
              component: <OrderInvoiceActions actions={moreActions} />,
            },
          ]
        : undefined,
      contentContainerStyle: navigationHeaderContainerStyle,
      contentFooter: (
        <View style={styles.navigationFooterContainer}>
          <View collapsable={false}>
            {invoiceUnitCount ? (
              <>
                <Text style={TYPOGRAPHY.small}>{i18n.t('units')}</Text>
                <Text style={styles.orderUnitCount}>
                  {formatUnitCount(invoiceUnitCount)}
                </Text>
              </>
            ) : null}
          </View>
          <OrderDetailPrices
            outstandingBalance={formatCurrency(currentBalance, currencyCode)}
            partialPaymentAmount={partialPaymentDisplay}
            totalPrice={totalBalance}
          />
        </View>
      ),
      contentStyle: styles.navigationHeaderContentLarge,
      title: (
        <View style={styles.navigationHeaderTitleLarge}>
          <View style={styles.titleColumn}>
            {renderInvoiceTitle()}
            {renderOrderSummaryComponent()}
          </View>
          {isLargeLayout ? (
            <View style={styles.titleRow}>
              {canPayInvoice ? (
                <View style={styles.invoiceButtonContainer}>{payButton}</View>
              ) : null}
              {showInvoicePDF ? (
                <OrderInvoicePDFButton
                  onPress={async () => await openInvoicePDF()}
                  trackingData={{
                    name: 'invoice_detail.view_pdf_button',
                    properties: {
                      orderInvoiceUUID: invoice.uuid,
                    },
                  }}
                />
              ) : null}
            </View>
          ) : null}
        </View>
      ),
      ...navigationHeaderOverrides,
    };
  }, [
    canPayInvoice,
    currencyCode,
    currentBalance,
    invoice,
    isLargeLayout,
    isSmallLayout,
    loading,
    moreActions,
    navigationHeaderContainerStyle,
    navigationHeaderOverrides,
    openInvoicePDF,
    order,
    orderFulfillmentDisplayName,
    partialPaymentDisplay,
    payButton,
    renderPayable,
    showInvoicePDF,
    styles,
    totalBalance,
  ]);

  return (
    <PageContainer
      animateNavigationHeader
      navigationHeaderProps={navigationHeaderProps}
    >
      {(scrollViewProps) =>
        loading ? (
          <View
            style={[
              scrollViewProps.contentContainerStyle,
              styles.staticContentContainer,
            ]}
          >
            <ActivityIndicator testID="loading-indicator" />
          </View>
        ) : !hasResponse && isOfflineModeActive ? (
          <View
            style={[
              scrollViewProps.contentContainerStyle,
              styles.staticContentContainer,
            ]}
          >
            <OfflineComponent />
          </View>
        ) : invoice ? (
          <>
            <InvoiceDetailList
              invoiceLineItems={invoice.order_invoice_line_items}
              scrollViewProps={scrollViewProps}
            />
            {!isLargeLayout ? (
              <View
                style={[
                  styles.invoiceBottomWrapper,
                  {
                    paddingBottom: Math.max(bottom, SCREEN_PADDING),
                  },
                ]}
              >
                {showCheckInEntry ? (
                  <CheckInButton
                    existingCheckIn={existingCheckIn}
                    invoiceUUID={invoice.uuid}
                    trackingData={{
                      name: 'invoice_detail.check_in_button',
                      properties: {
                        orderInvoiceUUID: invoice.uuid,
                      },
                    }}
                  />
                ) : (
                  <>
                    <View style={styles.orderSummaryRows}>
                      <View style={styles.orderSummaryRow}>
                        <Typography variant="paragraph-base">
                          {i18n.t('outstanding_balance')}
                        </Typography>
                        <Typography variant="heading-md">
                          {formatCurrency(currentBalance, currencyCode)}
                        </Typography>
                      </View>
                      {partialPaymentDisplay ? (
                        <View style={styles.orderSummaryRow}>
                          <Typography
                            color={theme.sk.color.text.tertiary}
                            variant="paragraph-md"
                          >
                            {i18n.t('partial_payment')}
                          </Typography>
                          <Typography
                            color={theme.sk.color.text.tertiary}
                            variant="paragraph-md"
                          >
                            {partialPaymentDisplay}
                          </Typography>
                        </View>
                      ) : null}
                    </View>
                    {canPayInvoice ? payButton : null}
                  </>
                )}
              </View>
            ) : null}
            {showMoreActionsSheet ? (
              <OrderInvoiceActionsSheet
                actions={moreActions}
                onClose={() => setShowMoreActionsSheet(false)}
              />
            ) : null}
          </>
        ) : null
      }
    </PageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  invoiceBottomWrapper: {
    backgroundColor: theme.colors.white,
    paddingHorizontal: SCREEN_PADDING_SMALL,
    paddingTop: SCREEN_PADDING_SMALL,
  },
  invoiceButtonContainer: {
    marginRight: 24,
  },
  invoiceInfoContainer: {
    borderBottomColor: theme.colors.stroke,
    borderBottomWidth: 1,
    paddingBottom: 16,
    paddingHorizontal: 8,
  },
  issuedOnDate: {
    marginBottom: 8,
  },
  navigationFooterContainer: {
    alignItems: 'center',
    borderTopColor: theme.colors.stroke,
    borderTopWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  navigationHeaderContentLarge: {
    height: 'auto',
  },
  navigationHeaderSubtitle: {
    ...FONTS.primarySemiBold,
    color: theme.colors.inactiveText,
  },
  navigationHeaderTitleLarge: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'space-between',
    paddingBottom: 8,
  },
  orderSummaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  orderSummaryRows: {
    paddingBottom: SCREEN_PADDING_SMALL,
  },
  orderUnitCount: {
    ...FONTS.primarySemiBold,
    fontSize: 20,
    lineHeight: 24,
  },
  staticContentContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  titleColumn: {
    flexDirection: 'column',
  },
  titleLeft: {
    marginLeft: 8,
  },
  titleRow: {
    flexDirection: 'row',
  },
  totalEmphasis: {
    ...FONTS.primarySemiBold,
    fontSize: 22,
    lineHeight: 24,
  },
  totalRow: {
    backgroundColor: theme.colors.white,
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginHorizontal: 4,
    paddingHorizontal: 4,
    paddingVertical: 16,
  },
  totalStandard: {
    ...FONTS.primary,
    color: theme.colors.primary,
    fontSize: 22,
    lineHeight: 24,
  },
}));
